import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Landmark, Upload, Loader2, Sparkles, Check, Lock } from "lucide-react";

const money = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export const BankImport = ({ categories, hasPlan, onCommitted }) => {
  const navigate = useNavigate();
  const ref = useRef(null);
  const [meta, setMeta] = useState(null);
  const [imp, setImp] = useState(null);
  const [picked, setPicked] = useState({});
  const [cats, setCats] = useState({});
  const [busy, setBusy] = useState(false);
  const [committing, setCommitting] = useState(false);

  const loadMeta = () => api.get("/vault/bank-imports").then((r) => setMeta(r.data)).catch(() => {});
  useEffect(() => { loadMeta(); }, []);

  const upload = async (f) => {
    if (!f) return;
    setBusy(true);
    try {
      const fd = new FormData(); fd.append("file", f);
      const { data } = await api.post("/vault/bank-import", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setImp(data);
      const p = {}; data.rows.forEach((r) => { if (r.likely_business && !r.already_added) p[r.row_id] = true; });
      setPicked(p); setCats({});
      toast.success(`${data.row_count} transactions read · ${data.flagged_count} look like business expenses`);
      loadMeta();
    } catch (e) { toast.error(e.response?.data?.detail || "Import failed"); }
    finally { setBusy(false); if (ref.current) ref.current.value = ""; }
  };

  const commit = async () => {
    const ids = Object.keys(picked).filter((k) => picked[k]);
    if (!ids.length) { toast.error("Tick at least one transaction"); return; }
    setCommitting(true);
    try {
      const { data } = await api.post(`/vault/bank-imports/${imp.import_id}/commit`, { row_ids: ids, categories: cats });
      toast.success(`${data.added} expense${data.added === 1 ? "" : "s"} added to your vault`);
      setImp(null); onCommitted?.(); loadMeta();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not add"); } finally { setCommitting(false); }
  };

  const exhausted = meta && !meta.unlimited && meta.remaining === 0;
  const selectedTotal = imp ? imp.rows.filter((r) => picked[r.row_id]).reduce((s, r) => s + r.amount, 0) : 0;

  return (
    <div className="bg-white border border-[#E5E5E5] rounded-2xl p-6" data-testid="bank-import">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <div className="flex items-center gap-2 text-[#114A31]"><Landmark className="h-5 w-5" /><span className="font-heading font-semibold">Bank & card import</span></div>
          <p className="mt-1 text-sm text-[#595D5A]">Export a CSV from your bank or card, and AI flags the likely business expenses. Tick the ones to add — no receipts needed.</p>
        </div>
        {meta && <span className="text-xs text-[#595D5A]" data-testid="bank-import-remaining">{meta.unlimited ? "Unlimited imports" : `${meta.remaining} of ${meta.limit} free this month`}</span>}
      </div>

      {!imp && (
        exhausted ? (
          <div className="mt-4 bg-[#F4F5F3] rounded-xl p-4 flex items-start gap-3" data-testid="bank-import-locked">
            <Lock className="h-4 w-4 text-[#595D5A] mt-0.5" />
            <div className="text-sm"><p className="font-semibold text-[#1C1C1C]">You've used this month's free import</p><p className="text-xs text-[#595D5A]">Every ScorpPath plan includes unlimited statement imports.</p>
              <Button onClick={() => navigate("/#pricing")} size="sm" variant="outline" data-testid="bank-import-upgrade" className="mt-2 rounded-full border-[#114A31] text-[#114A31] h-8 text-xs">See plans</Button></div>
          </div>
        ) : (
          <>
            <input ref={ref} type="file" accept=".csv,text/csv" className="hidden" data-testid="bank-file-input" onChange={(e) => upload(e.target.files?.[0])} />
            <Button onClick={() => ref.current?.click()} disabled={busy} variant="outline" data-testid="bank-upload-btn"
              className="mt-4 w-full rounded-xl border-dashed border-[#114A31] text-[#114A31] hover:bg-[#E6F0EB] h-12">
              {busy ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> <Sparkles className="h-4 w-4 mr-1" /> Reading & flagging transactions…</> : <><Upload className="h-4 w-4 mr-2" /> Upload statement CSV</>}
            </Button>
          </>
        )
      )}

      {imp && (
        <div className="mt-4" data-testid="bank-import-review">
          <p className="text-xs text-[#595D5A] mb-2">{imp.filename} · {imp.row_count} transactions · <span className="text-[#114A31] font-semibold">{imp.flagged_count} flagged as business</span> (pre-ticked — untick anything personal)</p>
          <ul className="space-y-1.5 max-h-96 overflow-y-auto pr-1" data-testid="bank-rows">
            {imp.rows.map((r) => (
              <li key={r.row_id} className={`flex items-center gap-2 rounded-xl px-3 py-2 text-sm ${r.already_added ? "bg-[#F4F5F3] opacity-60" : picked[r.row_id] ? "bg-[#E6F0EB]" : "bg-[#F9F9F8]"}`} data-testid={`bank-row-${r.row_id}`}>
                <Checkbox checked={!!picked[r.row_id]} disabled={r.already_added} onCheckedChange={(v) => setPicked({ ...picked, [r.row_id]: !!v })} data-testid={`bank-pick-${r.row_id}`} className="border-[#114A31] data-[state=checked]:bg-[#114A31]" />
                <div className="flex-1 min-w-0">
                  <p className="truncate text-[#1C1C1C]">{r.vendor}<span className="text-[#595D5A] text-xs"> · {r.date}</span>{r.already_added && <span className="ml-2 text-[10px] text-[#595D5A]">already in vault</span>}</p>
                  <p className="text-[11px] text-[#595D5A] truncate">{r.reason}</p>
                </div>
                <Select value={cats[r.row_id] || r.category} onValueChange={(v) => setCats({ ...cats, [r.row_id]: v })}>
                  <SelectTrigger className="h-8 w-36 text-xs rounded-lg hidden sm:flex" data-testid={`bank-cat-${r.row_id}`}><SelectValue /></SelectTrigger>
                  <SelectContent>{categories.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select>
                <span className="font-semibold text-[#1C1C1C] shrink-0 w-20 text-right">{money(r.amount)}</span>
              </li>
            ))}
          </ul>
          <div className="mt-3 flex items-center justify-between gap-3 flex-wrap">
            <p className="text-sm text-[#595D5A]">{Object.values(picked).filter(Boolean).length} selected · <span className="font-semibold text-[#1C1C1C]" data-testid="bank-selected-total">{money(selectedTotal)}</span></p>
            <div className="flex gap-2">
              <Button onClick={() => setImp(null)} variant="ghost" className="rounded-full">Cancel</Button>
              <Button onClick={commit} disabled={committing} data-testid="bank-commit-btn" className="rounded-full bg-[#114A31] hover:bg-[#1A6B46] text-white">
                {committing ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Check className="h-4 w-4 mr-1.5" /> Add to vault</>}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BankImport;
